/**
 * Migrate old language selection config (v1.x) to new canvas format (v2.0)
 * @param {Object} oldConfig - The old configuration object
 * @returns {Object} The migrated configuration in v2.0 format
 */
export function migrateToCanvasFormat(oldConfig) {
  // Create the new config structure
  const newConfig = {
    agencyId: oldConfig.agencyId,
    enabled: oldConfig.enabled ?? true,
    version: 2.0,
    targetResolution: oldConfig.targetResolution || {
      width: 1080,
      height: 1920
    },
    autoSelectTimeout: oldConfig.autoSelectTimeout || {
      enabled: false,
      seconds: 10,
      defaultLanguage: 'fr'
    },
    defaultLanguage: oldConfig.defaultLanguage || 'fr',
    enabledLanguages: (oldConfig.enabledLanguages || []).map((lang) => ({
      code: lang.code,
      label: lang.label,
      flag: lang.flag,
      order: lang.order ?? 0,
      custom: false // Existing languages are marked as non-custom
    })),
    languages: {}
  };

  // If no enabled languages, add a default one
  if (newConfig.enabledLanguages.length === 0) {
    newConfig.enabledLanguages.push({
      code: 'fr',
      label: 'Français',
      flag: '🇫🇷',
      order: 0,
      custom: false
    });
  }

  // Create a default canvas config from the old branding settings
  const defaultCanvasConfig = createDefaultCanvasConfig(oldConfig);

  // Apply the canvas config to all enabled languages
  newConfig.enabledLanguages.forEach((lang) => {
    // Deep clone the default config for each language
    newConfig.languages[lang.code] = JSON.parse(JSON.stringify(defaultCanvasConfig));
  });

  return newConfig;
}

/**
 * Create a default canvas configuration from old branding settings
 * @param {Object} oldConfig - The old configuration object
 * @returns {Object} Canvas configuration with containers and blocks
 */
function createDefaultCanvasConfig(oldConfig) {
  const blocks = [];
  let blockYPosition = 400;

  const branding = oldConfig.branding || {};
  const header = branding.header || {};
  const logo = branding.logo || {};
  const buttonStyle = branding.buttonStyle || {};

  // Convert logo if present
  if (logo.show && logo.url) {
    blocks.push({
      id: 'logo-migrated',
      type: 'image',
      x: 540, // Center horizontally (1080 / 2)
      y: logo.position === 'top' ? 200 : 500,
      width: logo.size || 120,
      height: logo.size || 120,
      icon: logo.url,
      src: logo.url,
      fit: 'contain',
      opacity: 1
    });

    // Adjust starting position if logo is at top
    if (logo.position === 'top') {
      blockYPosition = 400;
    }
  }

  // Convert header text if present
  if (header.show && header.text) {
    blocks.push({
      id: 'header-migrated',
      type: 'text',
      x: 100,
      y: blockYPosition,
      width: 880,
      height: 100,
      text: header.text,
      fontSize: header.fontSize || 48,
      fontFamily: header.fontFamily || 'Plus Jakarta Sans',
      fontWeight: '700',
      color: header.color || '#1E293B',
      textAlign: 'center'
    });

    blockYPosition += 150;
  }

  // Convert language buttons
  const enabledLanguages = oldConfig.enabledLanguages || [];
  const buttonSpacing = 150;
  const buttonWidth = 400;
  const buttonHeight = 120;
  const buttonX = 340; // Center horizontally: (1080 - 400) / 2

  enabledLanguages.forEach((lang, idx) => {
    blocks.push({
      id: `lang-${lang.code}-migrated`,
      type: 'language_button',
      x: buttonX,
      y: blockYPosition + (idx * buttonSpacing),
      width: buttonWidth,
      height: buttonHeight,
      languageCode: lang.code,
      label: lang.label,
      flag: lang.flag,
      backgroundColor: buttonStyle.backgroundColor || '#FFFFFF',
      color: buttonStyle.textColor || '#1E293B',
      borderRadius: buttonStyle.borderRadius ?? 20,
      borderWidth: buttonStyle.borderWidth ?? 2,
      borderColor: buttonStyle.borderColor || '#E2E8F0',
      fontSize: 28,
      fontWeight: '600',
      fontFamily: 'Plus Jakarta Sans',
      textAlign: 'center',
      action: {
        type: 'language',
        queueId: null,
        url: ''
      }
    });
  });

  // Determine canvas background settings
  const canvasBackground = {
    type: branding.backgroundType || 'color'
  };

  if (branding.backgroundType === 'gradient' && branding.gradient) {
    canvasBackground.gradient = {
      color1: branding.gradient.color1 || '#667eea',
      color2: branding.gradient.color2 || '#764ba2',
      direction: branding.gradient.direction || 'to right'
    };
  } else if (branding.backgroundType === 'image' && branding.backgroundImage) {
    canvasBackground.image = {
      url: branding.backgroundImage,
      size: 'cover'
    };
  }

  return {
    branding: {
      colors: {
        background: branding.backgroundColor || '#F8FAFC'
      },
      canvasBackground: canvasBackground
    },
    layout: {
      screenPadding: 24
    },
    containers: [
      {
        type: 'language_selection_canvas',
        display: 'absolute',
        blocks: blocks
      }
    ]
  };
}

/**
 * Check if a config needs migration
 * @param {Object} config - Configuration to check
 * @returns {Boolean} True if migration is needed
 */
export function needsMigration(config) {
  return !config.version || config.version < 2.0;
}

/**
 * Create an empty v2.0 config
 * @param {String} agencyId - The agency ID
 * @returns {Object} Empty v2.0 configuration
 */
export function createEmptyV2Config(agencyId) {
  return {
    agencyId: agencyId,
    version: 2.0,
    enabled: true,
    enabledLanguages: [
      {
        code: 'fr',
        label: 'Français',
        flag: '🇫🇷',
        order: 0,
        custom: false
      }
    ],
    defaultLanguage: 'fr',
    languages: {
      fr: {
        branding: {
          colors: {
            background: '#F8FAFC'
          },
          canvasBackground: {
            type: 'color'
          }
        },
        layout: {
          screenPadding: 24
        },
        containers: [
          {
            type: 'language_selection_canvas',
            display: 'absolute',
            blocks: []
          }
        ]
      }
    },
    autoSelectTimeout: {
      enabled: false,
      seconds: 10,
      defaultLanguage: 'fr'
    },
    targetResolution: {
      width: 1080,
      height: 1920
    }
  };
}
